// Banner — the full-width SCOPE STRIP per COMPONENTS §Catalog.
//
// Banner vs Alert: Banner is a flat, full-width band at a layout boundary —
// border-bottom only, no rounding, calm — carrying a scope-level signal (a
// project's demo/trial mode; an entity's lifecycle constraint like "Suspended —
// read-only"). Alert is the INLINE contained callout (a rounded, bordered box
// that sits WITHIN content — a form error, a section notice). Same severity
// grammar (the Layer-3 tokens + icons + role), different role/shape — the
// standard split (Atlassian Banner vs SectionMessage; Polaris Banner vs inline).
//
// Scope = mount level: the broader the scope, the higher it sits. A project
// banner is a SHELL band (above the Navbar); an entity banner is the entity
// LAYOUT band (above the entity header). They STACK, broadest on top. A project
// band is PINNED with the nav (always visible — `ShellTop`); an entity band
// scrolls away as at-rest chrome (the condensed bar's status pill carries on).
//
// Banner is layout-agnostic (like Alert) — it does NOT cap to the page measure
// or apply the gutter. The CONSUMING band supplies those: wrap in `pageBox` to
// cap the fill, pass `pageGutter` via `className` to inset the content. The fill
// + border span the band's full width; the gutter is padding, so the bg still
// reaches the edge.
//
// Status is never colour alone (WCAG SC 1.4.1): the label text IS the signal;
// the fill reinforces it.

import type { ReactNode } from 'react';
import { icons } from './icons.js';

export type BannerVariant = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

export interface BannerProps {
  /** `neutral` (default) = the calm informational strip (project demo/trial).
   *  The severity variants echo Badge/Alert and ECHO the entity's status pill. */
  variant?: BannerVariant;
  /** Override the leading icon. Defaults to the variant's icon (none for neutral);
   *  pass `null` to suppress a severity icon. */
  icon?: ReactNode;
  /** `start` (default) for an entity constraint that aligns with the body;
   *  `center` for a global announcement (project demo/trial). */
  align?: 'start' | 'center';
  /** How it is announced — defaults to the variant's own (`warning` / `danger`
   *  are an assertive `alert`). PERSISTENT chrome that can mount already filled
   *  in (a project-scope strip pinned to every page) passes `status` whatever its
   *  tone: an alert there would be spoken on every entry. `none` = no role at
   *  all — for a strip whose announcement lives in a live region of its OWN that
   *  is mounted before its words (a region born already filled is not announced). */
  role?: 'alert' | 'status' | 'none';
  /** The signal text (caller-supplied, i18n'd) + optional inline action. */
  children: ReactNode;
  className?: string;
}

// fill = the Layer-3 soft recipe (10% tint, the same cut as Alert — banners are
// large fills, calmer than the 15% Badge pill). The separator is the neutral
// --color-border (border-bottom only), not a severity box border. Text ink stays
// --color-text-primary (the recipe clears WCAG AA per token); neutral is the
// calm surface-alt strip (the project-banner look), muted ink.
const variantStyle: Record<
  BannerVariant,
  { fill: string; icon: ReactNode | null; role: 'alert' | 'status' }
> = {
  neutral: {
    fill: 'bg-[var(--color-bg-surface-alt)] text-[var(--color-text-secondary)]',
    icon: null,
    role: 'status',
  },
  info: {
    fill: 'bg-[var(--color-info)]/10 text-[var(--color-text-primary)]',
    icon: <icons.statusInfo aria-hidden weight="fill" size={20} className="shrink-0 text-[var(--color-info)]" />,
    role: 'status',
  },
  success: {
    fill: 'bg-[var(--color-success)]/10 text-[var(--color-text-primary)]',
    icon: <icons.statusSuccess aria-hidden weight="fill" size={20} className="shrink-0 text-[var(--color-success)]" />,
    role: 'status',
  },
  warning: {
    fill: 'bg-[var(--color-warning)]/10 text-[var(--color-text-primary)]',
    icon: <icons.statusWarning aria-hidden weight="fill" size={20} className="shrink-0 text-[var(--color-warning)]" />,
    role: 'alert',
  },
  danger: {
    fill: 'bg-[var(--color-danger)]/10 text-[var(--color-text-primary)]',
    icon: <icons.statusError aria-hidden weight="fill" size={20} className="shrink-0 text-[var(--color-danger)]" />,
    role: 'alert',
  },
};

export function Banner({ variant = 'neutral', icon, align = 'start', role, children, className = '' }: BannerProps) {
  const v = variantStyle[variant];
  const leadingIcon = icon !== undefined ? icon : v.icon;
  return (
    <div
      role={role === 'none' ? undefined : (role ?? v.role)}
      className={`flex items-start gap-2 py-1.5 text-sm border-b border-[var(--color-border)] ${v.fill} ${align === 'center' ? 'justify-center' : ''} ${className}`}
    >
      {leadingIcon}
      {align === 'center' ? children : <div className="flex-1 min-w-0">{children}</div>}
    </div>
  );
}
